import {
  AwardingMethodCode,
  ModifierCode,
} from '@/queries/program_purses/schemas'
import { useReducer } from 'react'

type WizardStep =
  | 'choose_award_rule'
  | 'configure_rule'
  | 'success'
  | 'view_purse_settings'
  | 'special_needs_modifier'
  | 'modifier'

type RuleType = AwardingMethodCode

type UseDistributionSettingsWizardOptions = {
  initialRuleType?: RuleType | null
}

type WizardState = {
  currentStep: WizardStep
  selectedRuleType: RuleType | null
  completedRuleType: RuleType | null
  selectedModifierCode: ModifierCode | null
}

type WizardAction =
  | { type: 'SELECT_RULE'; ruleType: RuleType }
  | { type: 'COMPLETE_RULE'; ruleType: RuleType }
  | { type: 'BACK_TO_CHOOSE' }
  | { type: 'VIEW_ALL_SETTINGS' }
  | { type: 'NAVIGATE_TO_SPECIAL_NEEDS_MODIFIER' }
  | { type: 'RESET' }
  | { type: 'NAVIGATE_TO_MODIFIER'; code: ModifierCode }

const buildInitialState = (existingRule: RuleType | null): WizardState => {
  if (!existingRule) {
    return initialState
  }

  return {
    currentStep: 'view_purse_settings',
    selectedRuleType: existingRule,
    completedRuleType: existingRule,
    selectedModifierCode: null,
  }
}

const initialState: WizardState = {
  currentStep: 'choose_award_rule',
  selectedRuleType: null,
  completedRuleType: null,
  selectedModifierCode: null,
}

const wizardStateMachine = (
  state: WizardState,
  action: WizardAction
): WizardState => {
  switch (action.type) {
    case 'SELECT_RULE':
      return {
        ...state,
        currentStep: 'configure_rule',
        selectedRuleType: action.ruleType,
      }

    case 'COMPLETE_RULE':
      return {
        ...state,
        currentStep: 'success',
        completedRuleType: action.ruleType,
      }

    case 'BACK_TO_CHOOSE':
      return {
        ...state,
        currentStep: 'choose_award_rule',
        selectedRuleType: null,
      }

    case 'VIEW_ALL_SETTINGS':
      return {
        ...state,
        currentStep: 'view_purse_settings',
      }

    case 'NAVIGATE_TO_SPECIAL_NEEDS_MODIFIER':
      return {
        ...state,
        currentStep: 'special_needs_modifier',
      }

    case 'NAVIGATE_TO_MODIFIER':
      return {
        ...state,
        currentStep: 'modifier',
        selectedModifierCode: action.code,
      }

    case 'RESET':
      return initialState

    default:
      return state
  }
}

export const useDistributionSettingsWizard = (
  options?: UseDistributionSettingsWizardOptions
) => {
  const [wizardState, dispatch] = useReducer(
    wizardStateMachine,
    options?.initialRuleType ?? null,
    buildInitialState
  )

  const handleRuleSelection = (choice: RuleType) => {
    dispatch({ type: 'SELECT_RULE', ruleType: choice })
  }

  const handleBackToChoose = () => {
    dispatch({ type: 'BACK_TO_CHOOSE' })
  }

  const handleRuleComplete = () => {
    if (wizardState.selectedRuleType) {
      dispatch({
        type: 'COMPLETE_RULE',
        ruleType: wizardState.selectedRuleType,
      })
    }
  }

  const handleViewRule = () => {
    dispatch({ type: 'VIEW_ALL_SETTINGS' })
  }

  const handleChangeRule = () => {
    dispatch({ type: 'BACK_TO_CHOOSE' })
  }

  const handleNavigateToSpecialNeedsModifier = () => {
    dispatch({ type: 'NAVIGATE_TO_SPECIAL_NEEDS_MODIFIER' })
  }

  const handleNavigateToNonGradeModifier = (code: ModifierCode) => {
    dispatch({ type: 'NAVIGATE_TO_MODIFIER', code })
  }

  return {
    state: wizardState,
    handleRuleSelection,
    handleBackToChoose,
    handleRuleComplete,
    handleViewRule,
    handleChangeRule,
    handleNavigateToSpecialNeedsModifier,
    handleNavigateToNonGradeModifier,
  }
}
